// frontend/src/components/ai/VehicleAIPage.js
//
// The routed owner-facing front door for CROW's Claude-backed vehicle AI.
// Three intents, one page: diagnose a symptom, get a whole-vehicle briefing,
// or sanity-check a shop quote. Every result surfaces `confidence` and
// `basis` so a thin answer reads as thin, not as an oracle — see
// backend/schemas/vehicle_ai.py for why that's non-negotiable here.
import React, { useState } from 'react';
import {
  Stethoscope, ClipboardList, Receipt, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import vehicleAiApi from '../../services/vehicleAiApi';
import {
  PageHeader, Card, CardHeader, CardDivider, Badge, Button, Tabs,
  FormField, Input, Textarea,
} from '../common';
import CorvinArt from '../brand/CorvinArt';
import CorvinThinking from '../corvin/CorvinThinking';

const CONFIDENCE_STATUS = { high: 'success', medium: 'warning', low: 'danger' };
const SAFE_TO_DRIVE_STATUS = { yes: 'success', caution: 'warning', no: 'danger' };
const SAFE_TO_DRIVE_LABEL = { yes: 'Safe to drive', caution: 'Drive with caution', no: 'Not safe to drive' };
const SEVERITY_STATUS = { low: 'neutral', medium: 'warning', high: 'warning', critical: 'danger' };
const URGENCY_STATUS = { immediate: 'danger', soon: 'warning', routine: 'info' };
const ASSESSMENT_STATUS = { typical: 'success', low: 'info', high: 'danger', unclear: 'neutral' };
const VERDICT_STATUS = { reasonable: 'success', 'somewhat high': 'warning', high: 'danger', unclear: 'neutral' };

const EMPTY_VEHICLE_CTX = { vin: '', year: '', make: '', model: '', trim: '', mileage: '' };
const EMPTY_REQUEST_STATE = { loading: false, error: null, result: null };

// Strip empty fields and coerce numerics before sending to the backend —
// every field in the request schema is optional, so we only send what the
// owner actually filled in.
function buildVehicleContext(ctx) {
  const out = {};
  if (ctx.vin.trim()) out.vin = ctx.vin.trim();
  if (ctx.year) out.year = Number(ctx.year);
  if (ctx.make.trim()) out.make = ctx.make.trim();
  if (ctx.model.trim()) out.model = ctx.model.trim();
  if (ctx.trim.trim()) out.trim = ctx.trim.trim();
  if (ctx.mileage) out.mileage = Number(ctx.mileage);
  return out;
}

// ---------------------------------------------------------------------------
// Confidence / basis — required on every result, styled to look different
// at each level so a low-confidence answer cannot be mistaken for a
// confident one.
// ---------------------------------------------------------------------------
function ConfidenceBasis({ confidence, basis }) {
  return (
    <div className="flex flex-wrap items-center gap-2 pb-3 mb-4 border-b border-border-default">
      <Badge status={CONFIDENCE_STATUS[confidence] || 'neutral'}>{confidence} confidence</Badge>
      <span className="text-small text-ink-muted">{basis}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error handling — 400/429/503 each get calm, human wording. A 503 is the
// state a fresh environment shows by default (feature flag off), so it gets
// the least alarming treatment of the three, not the most.
// ---------------------------------------------------------------------------
function AIError({ error }) {
  const status = error?.response?.status;

  if (status === 503) {
    return (
      <div className="rounded-card border border-border-default bg-surface-sunken p-6 text-center">
        <ShieldCheck size={26} strokeWidth={1.5} className="text-ink-faint mx-auto mb-3" />
        <p className="text-card-title font-display text-ink">AI is temporarily unavailable</p>
        <p className="text-small text-ink-muted mt-1 max-w-sm mx-auto">
          This assistant may be disabled in this environment, at its daily usage limit, or briefly
          unreachable. Nothing on your end is broken — try again shortly.
        </p>
      </div>
    );
  }

  if (status === 429) {
    return (
      <div className="rounded-card border border-warning-border bg-warning-tint p-4 flex items-start gap-3">
        <AlertTriangle size={20} strokeWidth={1.5} className="text-warning mt-0.5 flex-shrink-0" />
        <p className="text-body text-ink-soft">
          You&apos;ve used this hour&apos;s allowance for AI requests. Try again in a bit.
        </p>
      </div>
    );
  }

  if (status === 400) {
    return (
      <div className="rounded-card border border-warning-border bg-warning-tint p-4 flex items-start gap-3">
        <AlertTriangle size={20} strokeWidth={1.5} className="text-warning mt-0.5 flex-shrink-0" />
        <p className="text-body text-ink-soft">
          That&apos;s too much detail for one request. Shorten it and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-card border border-danger-border bg-danger-tint p-4 flex items-start gap-3">
      <AlertTriangle size={20} strokeWidth={1.5} className="text-danger mt-0.5 flex-shrink-0" />
      <p className="text-body text-ink-soft">
        Something went wrong reaching the AI assistant. Please try again.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared, optional vehicle-context fields — all six map directly onto every
// endpoint's optional request fields.
// ---------------------------------------------------------------------------
function VehicleContextFields({ ctx, onChange }) {
  const set = (field) => (e) => onChange({ ...ctx, [field]: e.target.value });

  return (
    <Card className="mb-6">
      <CardHeader
        title="Vehicle details"
        subtitle="Optional — the more you fill in, the more specific the answer."
      />
      <CardDivider />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <FormField label="Year" className="col-span-1">
          <Input type="number" inputMode="numeric" placeholder="2018" value={ctx.year} onChange={set('year')} />
        </FormField>
        <FormField label="Make" className="col-span-1">
          <Input placeholder="Honda" value={ctx.make} onChange={set('make')} />
        </FormField>
        <FormField label="Model" className="col-span-1">
          <Input placeholder="Civic" value={ctx.model} onChange={set('model')} />
        </FormField>
        <FormField label="Trim" className="col-span-1">
          <Input placeholder="EX" value={ctx.trim} onChange={set('trim')} />
        </FormField>
        <FormField label="Mileage" className="col-span-1">
          <Input type="number" inputMode="numeric" placeholder="85000" value={ctx.mileage} onChange={set('mileage')} />
        </FormField>
        <FormField label="VIN" className="col-span-2 sm:col-span-1">
          <Input placeholder="17 characters" maxLength={17} value={ctx.vin} onChange={set('vin')} />
        </FormField>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Diagnose
// ---------------------------------------------------------------------------
function DiagnoseResultView({ result }) {
  return (
    <Card>
      <ConfidenceBasis confidence={result.confidence} basis={result.basis} />

      <div className="flex items-center gap-2 mb-4">
        <Badge status={SAFE_TO_DRIVE_STATUS[result.safe_to_drive] || 'neutral'}>
          {SAFE_TO_DRIVE_LABEL[result.safe_to_drive] || result.safe_to_drive}
        </Badge>
      </div>
      <p className="text-body text-ink-soft mb-5">{result.safe_to_drive_reason}</p>

      <div className="flex items-baseline gap-2 mb-5">
        <span className="text-label uppercase text-ink-muted">Estimated cost</span>
        <span className="text-body-strong font-mono tabular-nums text-ink">
          ${Math.round(result.estimated_cost_low).toLocaleString()}–${Math.round(result.estimated_cost_high).toLocaleString()} CAD
        </span>
      </div>

      <h3 className="text-card-title font-display text-ink mb-2">Possible causes</h3>
      <ul>
        {result.possible_causes.map((c, i) => (
          <li key={i} className="py-3 border-b border-border-default last:border-b-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-body-strong text-ink">{c.cause}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-small text-ink-muted">{c.likelihood} likelihood</span>
                <Badge status={SEVERITY_STATUS[c.severity] || 'neutral'}>{c.severity}</Badge>
              </div>
            </div>
            <p className="text-small text-ink-muted mt-1">{c.explanation}</p>
          </li>
        ))}
      </ul>

      {result.next_steps?.length > 0 && (
        <>
          <h3 className="text-card-title font-display text-ink mt-5 mb-2">Next steps</h3>
          <ol className="list-decimal list-inside space-y-1">
            {result.next_steps.map((step, i) => (
              <li key={i} className="text-body text-ink-soft">{step}</li>
            ))}
          </ol>
        </>
      )}
    </Card>
  );
}

function DiagnoseTab({ vehicleCtx, state, onSubmit }) {
  const [symptom, setSymptom] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!symptom.trim() || state.loading) return;
    onSubmit({ symptom: symptom.trim(), ...buildVehicleContext(vehicleCtx) });
  };

  return (
    <div className="space-y-4">
      <Card>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormField label="What's going on?" required>
            <Textarea
              rows={3}
              placeholder="e.g. Grinding noise from the front left when braking"
              value={symptom}
              onChange={(e) => setSymptom(e.target.value)}
            />
          </FormField>
          <Button type="submit" loading={state.loading} disabled={!symptom.trim()}>
            Diagnose
          </Button>
        </form>
      </Card>

      {state.loading && <CorvinThinking />}
      {!state.loading && state.error && <AIError error={state.error} />}
      {!state.loading && state.result && <DiagnoseResultView result={state.result} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Briefing
// ---------------------------------------------------------------------------
function BriefResultView({ result }) {
  return (
    <Card>
      <ConfidenceBasis confidence={result.confidence} basis={result.basis} />

      <p className="text-body text-ink-soft mb-5">{result.summary}</p>

      {result.priority_actions?.length > 0 && (
        <>
          <h3 className="text-card-title font-display text-ink mb-2">Priority actions</h3>
          <ul className="mb-5">
            {result.priority_actions.map((a, i) => (
              <li key={i} className="py-3 border-b border-border-default last:border-b-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-body-strong text-ink">{a.action}</span>
                  <Badge status={URGENCY_STATUS[a.urgency] || 'neutral'}>{a.urgency}</Badge>
                </div>
                <p className="text-small text-ink-muted mt-1">{a.reason}</p>
              </li>
            ))}
          </ul>
        </>
      )}

      {result.maintenance_due?.length > 0 && (
        <>
          <h3 className="text-card-title font-display text-ink mb-2">Maintenance due</h3>
          <ul className="list-disc list-inside space-y-1 mb-5">
            {result.maintenance_due.map((item, i) => (
              <li key={i} className="text-body text-ink-soft">{item}</li>
            ))}
          </ul>
        </>
      )}

      {result.value_context && (
        <>
          <h3 className="text-card-title font-display text-ink mb-2">Value context</h3>
          <p className="text-body text-ink-soft">{result.value_context}</p>
        </>
      )}
    </Card>
  );
}

function BriefTab({ vehicleCtx, state, onSubmit }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (state.loading) return;
    onSubmit(buildVehicleContext(vehicleCtx));
  };

  return (
    <div className="space-y-4">
      <Card>
        <form onSubmit={handleSubmit} className="space-y-3">
          <p className="text-body text-ink-soft">
            Get a whole-vehicle summary — priority actions, maintenance coming due, and a value
            snapshot — built from the vehicle details above.
          </p>
          <Button type="submit" loading={state.loading}>Get briefing</Button>
        </form>
      </Card>

      {state.loading && <CorvinThinking />}
      {!state.loading && state.error && <AIError error={state.error} />}
      {!state.loading && state.result && <BriefResultView result={state.result} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Check a quote
// ---------------------------------------------------------------------------
function QuoteStandingNote() {
  return (
    <div className="rounded-card border border-border-default bg-surface-sunken p-4 mb-4 flex items-start gap-3">
      <AlertTriangle size={20} strokeWidth={1.5} className="text-ink-muted mt-0.5 flex-shrink-0" />
      <p className="text-small text-ink-soft">
        This is a sanity check against typical repair patterns — <strong>not a verified market
        rate</strong>. CROW does not hold live parts or labour pricing data; treat this as a second
        opinion, not a quote.
      </p>
    </div>
  );
}

function QuoteResultView({ result }) {
  return (
    <Card>
      <ConfidenceBasis confidence={result.confidence} basis={result.basis} />

      <div className="flex items-center gap-2 mb-5">
        <span className="text-label uppercase text-ink-muted">Overall verdict</span>
        <Badge status={VERDICT_STATUS[result.overall_verdict] || 'neutral'}>{result.overall_verdict}</Badge>
      </div>

      <h3 className="text-card-title font-display text-ink mb-2">Line items</h3>
      <ul className="mb-5">
        {result.line_items.map((li, i) => (
          <li key={i} className="py-3 border-b border-border-default last:border-b-0 flex items-start justify-between gap-3">
            <div>
              <p className="text-body-strong text-ink">{li.item}</p>
              <p className="text-small text-ink-muted mt-0.5">{li.note}</p>
            </div>
            <Badge status={ASSESSMENT_STATUS[li.assessment] || 'neutral'} className="flex-shrink-0">
              {li.assessment}
            </Badge>
          </li>
        ))}
      </ul>

      {result.concerns?.length > 0 && (
        <>
          <h3 className="text-card-title font-display text-ink mb-2">Concerns</h3>
          <ul className="list-disc list-inside space-y-1">
            {result.concerns.map((c, i) => (
              <li key={i} className="text-body text-ink-soft">{c}</li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function QuoteTab({ vehicleCtx, state, onSubmit }) {
  const [quoteText, setQuoteText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!quoteText.trim() || state.loading) return;
    onSubmit({ quote_text: quoteText.trim(), ...buildVehicleContext(vehicleCtx) });
  };

  return (
    <div className="space-y-4">
      <QuoteStandingNote />
      <Card>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormField label="Paste the quote" required>
            <Textarea
              rows={6}
              placeholder="Paste the line items and prices from your shop quote here"
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
            />
          </FormField>
          <Button type="submit" loading={state.loading} disabled={!quoteText.trim()}>
            Check quote
          </Button>
        </form>
      </Card>

      {state.loading && <CorvinThinking />}
      {!state.loading && state.error && <AIError error={state.error} />}
      {!state.loading && state.result && <QuoteResultView result={state.result} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const TABS = [
  { key: 'diagnose', label: 'Diagnose', icon: Stethoscope },
  { key: 'brief', label: 'Briefing', icon: ClipboardList },
  { key: 'quote', label: 'Check a Quote', icon: Receipt },
];

export default function VehicleAIPage() {
  const [activeTab, setActiveTab] = useState('diagnose');
  const [vehicleCtx, setVehicleCtx] = useState(EMPTY_VEHICLE_CTX);

  const [diagnoseState, setDiagnoseState] = useState(EMPTY_REQUEST_STATE);
  const [briefState, setBriefState] = useState(EMPTY_REQUEST_STATE);
  const [quoteState, setQuoteState] = useState(EMPTY_REQUEST_STATE);

  const runDiagnose = async (payload) => {
    setDiagnoseState({ loading: true, error: null, result: null });
    try {
      const result = await vehicleAiApi.diagnose(payload);
      setDiagnoseState({ loading: false, error: null, result });
    } catch (error) {
      setDiagnoseState({ loading: false, error, result: null });
    }
  };

  const runBrief = async (payload) => {
    setBriefState({ loading: true, error: null, result: null });
    try {
      const result = await vehicleAiApi.brief(payload);
      setBriefState({ loading: false, error: null, result });
    } catch (error) {
      setBriefState({ loading: false, error, result: null });
    }
  };

  const runQuote = async (payload) => {
    setQuoteState({ loading: true, error: null, result: null });
    try {
      const result = await vehicleAiApi.reviewQuote(payload);
      setQuoteState({ loading: false, error: null, result });
    } catch (error) {
      setQuoteState({ loading: false, error, result: null });
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        leading={<CorvinArt pose="avatar" size={48} />}
        title="Ask Corvin"
        subtitle="Ask about a symptom, get a whole-vehicle briefing, or sanity-check a shop quote."
      />

      <div className="mb-6">
        <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <VehicleContextFields ctx={vehicleCtx} onChange={setVehicleCtx} />

      {activeTab === 'diagnose' && (
        <DiagnoseTab vehicleCtx={vehicleCtx} state={diagnoseState} onSubmit={runDiagnose} />
      )}
      {activeTab === 'brief' && (
        <BriefTab vehicleCtx={vehicleCtx} state={briefState} onSubmit={runBrief} />
      )}
      {activeTab === 'quote' && (
        <QuoteTab vehicleCtx={vehicleCtx} state={quoteState} onSubmit={runQuote} />
      )}
    </div>
  );
}
